// Browser shim for node:fs/promises used by the verifier.
//
// The src/lib/ verifier reads receipt files through node:fs/promises.
// In the browser there is no filesystem; reads are routed to an
// in-memory virtual filesystem populated by the receipt-input adapters
// (directory drag-and-drop, ZIP upload, JSON paste, future URL fetch).
//
// Registry paths (registry/, schemas/, profiles/) fall through to
// static fetches against the deployed origin so that schema and
// profile validation work for receipts that reference the canonical
// registry shipped with this repo.
//
// vite.config.ts aliases both node:fs and node:fs/promises to this
// module, so anything node:fs needs (currently just existsSync) lives
// here too.

import { getFile, hasFile, listDirectory, fetchRegistryFile } from "./virtual-fs.js";

class FsError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
    this.name = "FsError";
  }
}

export interface ReadFileOptions {
  encoding?: BufferEncoding;
}

export async function readFile(
  filePath: string,
  options?: BufferEncoding | ReadFileOptions
): Promise<string> {
  const encoding = typeof options === "string" ? options : (options?.encoding ?? "utf8");
  if (encoding !== "utf8" && encoding !== "utf-8") {
    throw new FsError(
      "EUNSUPPORTED",
      `Browser verifier shim only supports utf8 readFile, got ${encoding}.`
    );
  }
  if (hasFile(filePath)) {
    return getFile(filePath)!;
  }
  const fromRegistry = await fetchRegistryFile(filePath);
  if (fromRegistry !== undefined) {
    return fromRegistry;
  }
  throw new FsError("ENOENT", `Browser verifier shim could not find file: ${filePath}`);
}

export interface DirentLike {
  name: string;
  isFile(): boolean;
  isDirectory(): boolean;
}

export async function readdir(
  dirPath: string,
  options?: { withFileTypes?: boolean }
): Promise<string[] | DirentLike[]> {
  const entries = listDirectory(dirPath);
  if (!entries) {
    throw new FsError("ENOENT", `Browser verifier shim could not find directory: ${dirPath}`);
  }
  if (options?.withFileTypes) {
    return entries.map(
      (name): DirentLike => ({
        name,
        isFile: () => true,
        isDirectory: () => false
      })
    );
  }
  return entries;
}

export async function mkdir(): Promise<void> {
  // No-op in the browser. The verifier writes a
  // verification-report.json after a successful run; we don't
  // persist anything in the browser verifier UI, so silently accept.
}

export async function writeFile(): Promise<void> {
  // No-op in the browser. See mkdir comment.
}

export async function rm(): Promise<void> {
  // No-op in the browser.
}

export async function appendFile(): Promise<void> {
  // No-op in the browser.
}

export async function cp(): Promise<void> {
  // No-op in the browser.
}

export async function stat(filePath: string): Promise<{
  isFile(): boolean;
  isDirectory(): boolean;
}> {
  if (hasFile(filePath)) {
    return { isFile: () => true, isDirectory: () => false };
  }
  const entries = listDirectory(filePath);
  if (entries) {
    return { isFile: () => false, isDirectory: () => true };
  }
  throw new FsError("ENOENT", `Browser verifier shim could not stat: ${filePath}`);
}

// Synchronous existence check. The only reachable consumer in the
// verifier is src/lib/paths.ts findProjectRoot, which walks up the
// directory tree calling existsSync(path/to/package.json) at each
// level. In the browser there is no real package.json on any
// filesystem-style path, but findProjectRoot needs SOMETHING to be
// true so it terminates instead of walking to "/" and throwing at
// module load.
//
// Returning true for any path whose basename is "package.json" makes
// findProjectRoot stop at its first iteration and use moduleDir as
// PROJECT_ROOT. The resulting PROJECT_ROOT is a browser-URL-y string
// with no real filesystem meaning, but the verifier never reads
// files via PROJECT_ROOT directly: every read goes through readFile
// above, which checks the in-memory virtual-fs (populated by the
// receipt-input adapters) and then falls through to
// fetchRegistryFile for registry / schemas / profiles paths. That
// fallback uses startsWith() against a normalized path, not the
// PROJECT_ROOT prefix.
//
// For any non-package.json path the function honestly answers
// against the virtual-fs: file present, directory present, or
// neither.
export function existsSync(filePath: string): boolean {
  if (filePath === "package.json" || filePath.endsWith("/package.json")) {
    return true;
  }
  if (hasFile(filePath)) return true;
  if (listDirectory(filePath)) return true;
  return false;
}

export default {
  readFile,
  readdir,
  mkdir,
  writeFile,
  rm,
  appendFile,
  cp,
  stat,
  existsSync
};
